import Vue from 'vue';

import pixAxios from '@/axios/pixAxios.js';
import pixAxiosSyncRequests from '@/axios/pixAxiosSyncRequests.js';

import documentUtils from '@/services/document/document.utils.js';
import comparableUtils from '@/services/propertyComparables/comparable.utils';

import modeConst from '@/consts/mode.const';
import statusConst from '@/consts/status.const.js';
import documentFormatConst from '@/consts/documentFormat.const.js';
import mongoTypeConst from '@/consts/mongoType.const.js';
import dataTypeConst from '@/consts/dataType.const.js';

const researchModule = {
  namespaced: true,

  state: {
    selectedSearch: null,
    tabSaveStatus: {},
    openSubmitModal: false,
    openNewCompModal: false,
    comparableIdForCopy: null,
    basketIdForCopy: null,
    // Booklet
    openBookletModal: false,
    bookletComparables: [],
    fetchBookletComparablesStatus: statusConst.PENDING,
  },

  actions: {
    async loadResearchTabs({ dispatch, rootGetters, getters }) {
      if (rootGetters['genericConfigModule/getGenericTabs'](modeConst.RESEARCH)?.length) return;
      try {
        const { data } = await pixAxios.get('/api/GenericConfig/GetResearchTabs', { params: { instance: rootGetters['authModule/fullInstance'], lang: rootGetters.langCode } });
        await dispatch('genericConfigModule/setGenericTabs', { data, mode: modeConst.RESEARCH }, { root: true });
      } catch (error) {
        dispatch('toastModule/error', 'Error loading research tabs', { root: true });
      }
    },

    async fetchSearchById({ commit, dispatch }, searchId) {
      dispatch('loadingOverlayModule/setIsLoading', true, { root: true });
      try {
        const { data } = await pixAxios.post('/api/Searches/GetSearchById', { RowId: searchId });
        commit('setSelectedSearch', data);
      } catch (error) {
        dispatch('toastModule/error', 'Error loading SearchById', { root: true });
      }
      dispatch('loadingOverlayModule/setIsLoading', false, { root: true });
    },

    async downloadDocument({ state, dispatch, rootGetters }, downloadDocumentParams) {
      dispatch('loadingOverlayModule/setIsLoading', true, { root: true });

      const downloadDocsArray = downloadDocumentParams.checkboxes
        .filter(({ value }) => value)
        .map(({ id }) => {
          if (id === documentFormatConst.PROPERTY_BROCHURE) {
            return pixAxios.post('/api/Document/ExportBrochures', {
              SearchId: state?.selectedSearch?.id,
              UrlList: downloadDocumentParams.selectedBrochuresUrl,
              Locale: `${rootGetters.langCode}-${rootGetters['authModule/instance']}`,
              Format: id,
            });
          } else {
            return pixAxios.put('/api/Searches/ExportSearchComps', {
              SearchId: state?.selectedSearch?.id,
              Comparables: downloadDocumentParams.selectedRowsId,
              Locale: `${rootGetters.langCode}-${rootGetters['authModule/instance']}`,
              Format: id,
            });
          }
        });

      try {
        const response = await Promise.all(downloadDocsArray);
        const filterResponse = response.filter(({ status }) => status !== 204);

        filterResponse.forEach((doc) => {
          // From the response we get the configuration sent to the API, to check what the format is, at that moment in the loop. And apply the correct title for the downloaded document
          const formatFile = JSON.parse(doc.config.data);

          let documentTitle;
          if (formatFile.Format === documentFormatConst.DETAIL_WORD) {
            documentTitle = downloadDocumentParams.isLease ? 'Letting_Detailed_Comps' : 'Investment_Detailed_Comps';
          } else {
            documentTitle = downloadDocumentParams.isLease ? 'Letting_Comps' : 'Investment_Comps';
          }

          documentUtils.exportDocument(doc, documentTitle);
        });
      } catch (error) {
        const detailError = JSON.parse(error?.response?.data?.detail);
        const detailErrorMessage = JSON.parse(detailError.detail);
        dispatch('toastModule/error', detailErrorMessage.message, { root: true });
      } finally {
        dispatch('loadingOverlayModule/setIsLoading', false, { root: true });
      }
    },

    setSelectedSearch({ commit }, search) {
      commit('setSelectedSearch', search);
    },

    async saveSearch({ commit }, search) {
      try {
        const { data } = await pixAxiosSyncRequests.put('/api/Searches/SaveSearch', search);
        commit('setSelectedSearch', data);
      } catch {
        // Nothing
      }
    },

    startSaveSelectedTab({ commit, getters }, payload) {
      commit('setStartSaveSelectedTab', {
        ...payload,
        selectedTab: getters.getSelectedTab,
      });
    },

    finishSaveSelectedTab({ commit, getters }) {
      commit('setFinishSaveSelectedTab', getters.getSelectedTab);
    },

    setOpenBookletModal({ commit }, open) {
      commit('setOpenBookletModal', open);
    },

    setOpenSubmitModal({ commit }, { open }) {
      commit('setOpenSubmitModal', open);
    },

    clearSelectedSearch({ commit }) {
      commit('setSelectedSearch', null);
    },

    setOpenNewCompModal({ commit }, open) {
      commit('setOpenNewCompModal', open);
    },

    setComparableIdForCopy({ commit }, comparableId) {
      commit('setComparableIdForCopy', comparableId);
    },

    setBasketIdForCopy({ commit }, comparableId) {
      commit('setBasketIdForCopy', comparableId);
    },

    async fetchBookletComparables({ state, commit, rootGetters }, { period }) {
      commit('setFetchBookletComparablesStatus', statusConst.LOADING);

      try {
        const response = await pixAxios.post('api/Comparables/GetComparablesWithBasket', {
          SearchWithoutPolygon: true,
          filter: {
            LogicOperation: 'OR',
            Filters: [
              {
                LogicOperation: 'AND',
                Filters: [
                  {
                    Values: [826, 238, 831, 832, 833],
                    Operation: {
                      ParamId: dataTypeConst.SELECT.PARAM_ID,
                      ValueCode: dataTypeConst.SELECT.EQUALS,
                    },
                    Field: {
                      MetricKey: 'CountryCode',
                      MongoType: mongoTypeConst.INTEGER,
                    },
                  },
                  {
                    Values: ['97'],
                    Operation: {
                      ParamId: dataTypeConst.SELECT.PARAM_ID,
                      ValueCode: dataTypeConst.SELECT.EQUALS,
                    },
                    Field: { MetricKey: 'FileTypeID', MongoType: mongoTypeConst.INTEGER },
                  },
                  {
                    Values: ['01'],
                    Operation: {
                      ParamId: dataTypeConst.SELECT.PARAM_ID,
                      ValueCode: dataTypeConst.SELECT.EQUALS,
                    },
                    Field: {
                      MetricKey: 'OperationTypeID',
                      MongoType: mongoTypeConst.INTEGER,
                    },
                  },
                  {
                    Values: [period],
                    Operation: {
                      ParamId: dataTypeConst.STRING.PARAM_ID,
                      ValueCode: dataTypeConst.STRING.CONTAINS,
                    },
                    Field: {
                      MetricKey: 'InvestmentBookletPeriod',
                      MongoType: mongoTypeConst.STRING,
                    },
                  },
                  {
                    Values: ['true'],
                    Operation: {
                      ParamId: dataTypeConst.SELECT.PARAM_ID,
                      ValueCode: dataTypeConst.SELECT.EQUALS,
                    },
                    Field: {
                      MetricKey: 'IsIncludedInInvestmentBooklet',
                      MongoType: mongoTypeConst.BOOLEAN,
                    },
                  },
                  // Any comparables with Transaction Status of Unknown or Withdrawn will be ignored.
                  // {
                  //   Values: ['0001', '0002', '0003'],
                  //   Operation: {
                  //     ParamId: dataTypeConst.SELECT.PARAM_ID,
                  //     ValueCode: dataTypeConst.SELECT.EQUALS,
                  //   },
                  //   Field: {
                  //     MetricKey: 'TransactionType',
                  //     MongoType: mongoTypeConst.STRING,
                  //   },
                  // },
                ],
              },
            ],
          },
          sortFld: null,
          sortDir: null,
          sortList: [
            {
              sortDir: 'desc',
              sortFld: 'PropertyTypeCode',
            },
          ],
          circle: null,
          requestId: 1,
          lang: rootGetters.langCode,
          relationId: state.selectedSearch.id,
          DistanceUnits: 1,
          skip: 0,
          take: 999999,
        });

        const {
          data: { Comparables },
        } = response;

        commit('setBookletComparables', Comparables);
      } catch (err) {
        console.error(err);
      } finally {
        commit('setFetchBookletComparablesStatus', statusConst.SUCCESS);
      }
    },

    clearBookletComparables({ commit }) {
      commit('setBookletComparables', []);
    },
  },

  mutations: {
    setStartSaveSelectedTab(state, { selectedTab, isSubmit, checkSubmit }) {
      Vue.set(state.tabSaveStatus, selectedTab, { isSubmit, checkSubmit });
    },

    setFinishSaveSelectedTab(state, selectedTab) {
      Vue.set(state.tabSaveStatus, selectedTab, false);
    },

    setOpenBookletModal(state, payload) {
      Vue.set(state, 'openBookletModal', payload);
    },

    setOpenSubmitModal(state, payload) {
      Vue.set(state, 'openSubmitModal', payload);
    },

    setSelectedSearch(state, payload) {
      Vue.set(state, 'selectedSearch', payload);
    },

    setOpenNewCompModal(state, payload) {
      Vue.set(state, 'openNewCompModal', payload);
    },

    setComparableIdForCopy(state, payload) {
      Vue.set(state, 'comparableIdForCopy', payload);
    },

    setBasketIdForCopy(state, payload) {
      Vue.set(state, 'basketIdForCopy', payload);
    },

    setBookletComparables(state, payload) {
      Vue.set(state, 'bookletComparables', payload);
    },

    setFetchBookletComparablesStatus(state, payload) {
      Vue.set(state, 'fetchBookletComparablesStatus', payload);
    },
  },

  getters: {
    getTabSaveStatus: (state) => (selectedTab) => {
      return state.tabSaveStatus[selectedTab];
    },

    getSelectedSearch(state) {
      return state.selectedSearch;
    },

    getSelectedTab(state) {
      return state?.selectedSearch?.TabTypeId ?? null;
    },

    getOpenSubmitModal(state) {
      return state.openSubmitModal;
    },

    getOpenNewCompModal(state) {
      return state.openNewCompModal;
    },

    getComparableIdForCopy(state) {
      return state.comparableIdForCopy;
    },

    getBasketIdForCopy(state) {
      return state.basketIdForCopy;
    },
  },
};

export default researchModule;
